import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewRef } from '@angular/core';
import { ExportacaoRegistro, ExportacaoService } from '../../../services/exportacao.service';
import { AuthService } from '../../../../../services/auth.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';

@Component({
  selector: 'app-exportacao',
  standalone: false,
  templateUrl: './exportacao.component.html',
  styleUrls: ['./exportacao.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportacaoComponent implements OnInit {
  registros: ExportacaoRegistro[] = [];
  carregando = false;
  erro: string | null = null;
  reenviandoIds = new Set<string>();
  desfazendoIds = new Set<string>();

  filtroExportado: '' | 'true' | 'false' = '';
  filtroCidade = '';
  filtroBairro = '';
  filtroNumeroOs = '';

  paginaAtual = 1;
  tamanhoPagina = 50;
  totalPaginas = 0;
  totalItens = 0;

  constructor(
    private readonly exportacaoService: ExportacaoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.buscar();
  }

  aplicarFiltros(): void {
    this.paginaAtual = 1;
    this.buscar();
  }

  limparFiltros(): void {
    this.filtroExportado = '';
    this.filtroCidade = '';
    this.filtroBairro = '';
    this.filtroNumeroOs = '';
    this.paginaAtual = 1;
    this.buscar();
  }

  irParaPagina(pagina: number): void {
    if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaAtual) {
      return;
    }

    this.paginaAtual = pagina;
    this.buscar();
  }

  paginaAnterior(): void {
    this.irParaPagina(this.paginaAtual - 1);
  }

  proximaPagina(): void {
    this.irParaPagina(this.paginaAtual + 1);
  }

  alterarTamanhoPagina(valor: number | string): void {
    const novoTamanho = Number(valor) || 50;
    if (novoTamanho === this.tamanhoPagina) {
      return;
    }

    this.tamanhoPagina = novoTamanho;
    this.paginaAtual = 1;
    this.buscar();
  }

  trackByRegistro(_: number, item: ExportacaoRegistro): string {
    return item.registroId;
  }

  getStatusExportacao(item: ExportacaoRegistro): string {
    const statusPainel = (item.statusExportacao ?? '').trim();
    if (statusPainel.length > 0) {
      return statusPainel;
    }

    return item.exportado ? 'Sucesso' : 'Pendente';
  }

  getClasseStatusExportacao(item: ExportacaoRegistro): string {
    const status = this.getStatusExportacao(item).toLowerCase();

    if (status === 'sucesso' || status === 'exportado') {
      return 'exportado-badge';
    }

    if (status === 'erro') {
      return 'erro-badge';
    }

    return 'pendente-badge';
  }

  podeReenviar(item: ExportacaoRegistro): boolean {
    if (!item.exportado) {
      return true;
    }

    return this.isUsuarioAdminOuSuperAdmin();
  }

  isErroExportacao(item: ExportacaoRegistro): boolean {
    return this.getStatusExportacao(item).toLowerCase() === 'erro';
  }

  getTextoAcao(item: ExportacaoRegistro): string {
    if (this.estaReenviando(item)) {
      return 'Enviando...';
    }

    if (item.exportado || this.isErroExportacao(item)) {
      return 'Reenviar';
    }

    return 'Enviar manual';
  }

  podeDesfazerExportacao(item: ExportacaoRegistro): boolean {
    return this.isUsuarioSuperAdmin() && item.exportado;
  }

  estaDesfazendo(item: ExportacaoRegistro): boolean {
    return this.desfazendoIds.has(item.registroId);
  }

  getTextoDesfazer(item: ExportacaoRegistro): string {
    return this.estaDesfazendo(item) ? 'Removendo...' : 'Remover sucesso';
  }

  private isUsuarioAdminOuSuperAdmin(): boolean {
    try {
      const role = (this.authService.getUserRoleSync() || '').trim().toLowerCase();
      return role === 'administrador' || role === 'superadministrador';
    } catch {
      return false;
    }
  }

  private isUsuarioSuperAdmin(): boolean {
    try {
      const role = (this.authService.getUserRoleSync() || '').trim().toLowerCase();
      return role === 'superadministrador';
    } catch {
      return false;
    }
  }

  estaReenviando(item: ExportacaoRegistro): boolean {
    return this.reenviandoIds.has(item.registroId);
  }

  reenviarRegistro(item: ExportacaoRegistro): void {
    if (!this.podeReenviar(item) || this.estaReenviando(item)) {
      return;
    }

    this.reenviandoIds.add(item.registroId);
    this.cdr.markForCheck();

    this.exportacaoService.reenviarRegistro(item.registroId, item.tipoRegistro)
      .subscribe({
        next: () => {
          this.reenviandoIds.delete(item.registroId);
          this.buscar();
        },
        error: (err) => {
          this.reenviandoIds.delete(item.registroId);
          this.erro = err?.error?.mensagem ?? 'Erro ao reenviar registro manualmente.';
          this.cdr.markForCheck();
        }
      });
  }

  async desfazerExportacaoSucesso(item: ExportacaoRegistro): Promise<void> {
    if (!this.podeDesfazerExportacao(item) || this.estaDesfazendo(item)) {
      return;
    }

    const confirmacao = await Swal.fire({
      title: 'Confirmar remocao',
      text: 'Deseja remover a marcacao de exportacao concluida deste registro para permitir reenvio?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    this.desfazendoIds.add(item.registroId);
    this.cdr.markForCheck();

    this.exportacaoService.desfazerExportacaoSucesso(item.registroId, item.tipoRegistro)
      .subscribe({
        next: () => {
          this.desfazendoIds.delete(item.registroId);
          this.buscar();
        },
        error: (err) => {
          this.desfazendoIds.delete(item.registroId);
          this.erro = err?.error?.mensagem ?? 'Erro ao remover a exportacao concluida do registro.';
          this.cdr.markForCheck();
        }
      });
  }

  getEndereco(item: ExportacaoRegistro): string {
    const partes = [item.logradouro, item.numero, item.bairro, item.cidade]
      .filter((p) => !!p && String(p).trim().length > 0)
      .map((p) => String(p).trim());

    return partes.length > 0 ? partes.join(', ') : '-';
  }

  private buscar(): void {
    this.carregando = true;
    this.erro = null;
    this.atualizarTela();

    this.exportacaoService.obterPainel({
      exportado: this.filtroExportado === '' ? null : this.filtroExportado === 'true',
      cidade: this.filtroCidade,
      bairro: this.filtroBairro,
      numeroOs: this.filtroNumeroOs,
      pagina: this.paginaAtual,
      tamanhoPagina: this.tamanhoPagina
    }).subscribe({
      next: (res) => {
        this.registros = Array.isArray(res?.itens) ? res.itens : [];
        this.totalItens = Number(res?.totalPesquisa ?? res?.totalItens ?? this.registros.length);
        this.totalPaginas = Number(res?.totalPaginas ?? 0);
        this.paginaAtual = Number(res?.paginaAtual ?? this.paginaAtual);
        this.tamanhoPagina = Number(res?.tamanhoPagina ?? this.tamanhoPagina);
        this.carregando = false;
        this.atualizarTela();
      },
      error: () => {
        this.registros = [];
        this.totalItens = 0;
        this.totalPaginas = 0;
        this.carregando = false;
        this.erro = 'Erro ao carregar o painel de exportacao.';
        this.atualizarTela();
      }
    });
  }

  private atualizarTela(): void {
    const viewRef = this.cdr as ViewRef;
    if (!viewRef.destroyed) {
      this.cdr.detectChanges();
    }
  }
}
